import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Code2, Plus, Loader2, Trash2, Copy, Download, Send, FileCode2, X } from "lucide-react";
import { toast } from "sonner";

const CYAN = "#00E5FF";
const MODES = [
  ["generate", "Generate"], ["explain", "Explain"], ["debug", "Debug"], ["refactor", "Refactor"],
  ["optimize", "Optimize"], ["convert", "Convert"], ["test", "Tests"], ["document", "Docs"],
];
const LANGS = ["auto", "javascript", "typescript", "python", "java", "go", "rust", "c#", "php", "sql", "html/css", "swift", "kotlin"];

// Splits markdown into text + code segments for rendering
function splitBlocks(text) {
  const parts = [];
  const re = /```([^\n`]*)\n([\s\S]*?)```/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", content: text.slice(last, m.index) });
    parts.push({ type: "code", info: m[1].trim(), content: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", content: text.slice(last) });
  return parts;
}

function CodeBlock({ info, content }) {
  const copy = () => { navigator.clipboard.writeText(content); toast.success("Copied"); };
  return (
    <div className="my-2 rounded-lg border border-white/[0.09] overflow-hidden bg-[#050608]">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]">
        <span className="text-[10px] font-mono text-white/40">{info || "code"}</span>
        <button onClick={copy} className="text-white/40 hover:text-white p-1" aria-label="Copy code"><Copy className="h-3 w-3" /></button>
      </div>
      <pre className="p-3 text-xs font-mono leading-relaxed overflow-x-auto text-[#9EE8FF] max-h-96 overflow-y-auto">{content}</pre>
    </div>
  );
}

export default function AlphaCoreCode() {
  const { refreshUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("generate");
  const [lang, setLang] = useState("auto");
  const [streaming, setStreaming] = useState(false);
  const [liveText, setLiveText] = useState("");
  const [viewFile, setViewFile] = useState(null);
  const scrollRef = useRef(null);

  const loadSessions = () => api.get("/code/sessions").then((r) => setSessions(r.data.items)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [session?.messages?.length, liveText]);

  const newSession = async () => {
    try { const { data } = await api.post("/code/sessions", {}); setSessions((s) => [data, ...s]); setSession(data); setViewFile(null); }
    catch { toast.error("Could not create session"); }
  };
  const open = async (id) => {
    try { const { data } = await api.get(`/code/sessions/${id}`); setSession(data); setViewFile(null); }
    catch { toast.error("Could not open session"); }
  };
  const remove = async (id) => {
    await api.delete(`/code/sessions/${id}`).catch(() => {});
    if (session?.session_id === id) setSession(null);
    loadSessions();
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe what you need");
    let sess = session;
    if (!sess) {
      const { data } = await api.post("/code/sessions", {});
      sess = data; setSession(data); setSessions((s) => [data, ...s]);
    }
    const userPrompt = prompt;
    setPrompt(""); setStreaming(true); setLiveText("");
    setSession((s) => ({ ...s, messages: [...(s.messages || []), { role: "user", content: userPrompt, mode }] }));
    try {
      const token = localStorage.getItem("alphacore_token");
      const res = await fetch(`${API_BASE}/code/sessions/${sess.session_id}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ prompt: userPrompt, mode, language: lang }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", full = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.token) { full += ev.token; setLiveText(full); }
          if (ev.error) throw new Error(ev.error);
          if (ev.done) {
            const { data } = await api.get(`/code/sessions/${sess.session_id}`);
            setSession(data); loadSessions(); refreshUser();
            if (ev.files?.length) toast.success(`${ev.files.length} file${ev.files.length > 1 ? "s" : ""} added to the file tree`);
          }
        }
      }
    } catch (e) {
      toast.error(e.message || "Generation failed");
      setSession((s) => s ? { ...s, messages: (s.messages || []).slice(0, -1) } : s);
    } finally { setStreaming(false); setLiveText(""); }
  };

  const copyFile = (f) => { navigator.clipboard.writeText(f.content); toast.success("Copied"); };
  const downloadFile = (f) => {
    const blob = new Blob([f.content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = f.path.split("/").pop();
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto kr-page" data-module="code">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline" style={{ color: CYAN }}>[ ALPHACORE CODE ]</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">AlphaCore Code</h1>
          <p className="text-[#9DA3B4] text-sm mt-1.5">Generate, analyze & improve code. Generated code is not executed — copy or download it to run.</p>
        </div>
        <Button onClick={newSession} className="rounded-xl h-10 px-5 font-semibold text-black border-0" style={{ background: CYAN }} data-testid="code-new-session-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Session
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Sessions */}
        <div className="lg:col-span-3 kr-card p-4 h-fit">
          <div className="kr-overline mb-3">SESSIONS</div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.session_id} className={`p-3 rounded-lg border flex items-start gap-2 ${session?.session_id === s.session_id ? "border-[#00E5FF]/40 bg-[#00E5FF]/5" : "border-white/[0.07] hover:border-white/20"}`}>
                <button onClick={() => open(s.session_id)} className="flex-1 min-w-0 text-left" data-testid={`code-session-${s.session_id}`}>
                  <div className="text-sm truncate">{s.title}</div>
                  <div className="text-[11px] text-white/35 mt-0.5">{new Date(s.updated_at).toLocaleDateString()}</div>
                </button>
                <button onClick={() => remove(s.session_id)} className="text-white/25 hover:text-red-300 p-1 shrink-0" aria-label="Delete session" data-testid={`code-session-delete-${s.session_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {sessions.length === 0 && <div className="text-xs text-white/35 py-4 text-center">No sessions yet.</div>}
          </div>
        </div>

        {/* Conversation */}
        <div className="lg:col-span-6 kr-card flex flex-col min-h-[560px] max-h-[calc(100vh-220px)]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5">
            {!session && !streaming && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <Code2 className="h-10 w-10" style={{ color: CYAN }} />
                <div className="font-display text-lg mt-4">What should we build?</div>
                <p className="text-sm text-white/45 mt-2 max-w-sm">Describe a component, script, API, schema, bug or refactor — complete files land in the file tree on the right.</p>
              </div>
            )}
            {(session?.messages || []).map((m, i) => (
              <div key={i} data-testid={`code-msg-${i}`}>
                {m.role === "user" ? (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-xl px-4 py-2.5 text-sm bg-white/[0.06] border border-white/[0.08]">
                      {m.mode && m.mode !== "generate" && <span className="text-[10px] font-mono uppercase tracking-wider mr-2" style={{ color: CYAN }}>{m.mode}</span>}
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm leading-relaxed text-white/85">
                    {splitBlocks(m.content).map((b, j) =>
                      b.type === "code" ? <CodeBlock key={j} info={b.info} content={b.content} /> : <p key={j} className="whitespace-pre-wrap my-1.5">{b.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
            {streaming && (
              <div className="text-sm leading-relaxed text-white/85" data-testid="code-streaming-output">
                {liveText ? splitBlocks(liveText).map((b, j) =>
                  b.type === "code" ? <CodeBlock key={j} info={b.info} content={b.content} /> : <p key={j} className="whitespace-pre-wrap my-1.5">{b.content}</p>
                ) : <Loader2 className="h-4 w-4 animate-spin" style={{ color: CYAN }} />}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/[0.07] p-3.5 space-y-2.5">
            <div className="flex gap-2 flex-wrap">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="bg-[#07080B] border-white/10 h-8 w-32 text-xs" data-testid="code-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#101219] border-white/10">
                  {MODES.map(([v, l]) => <SelectItem key={v} value={v} className="focus:bg-white/5">{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="bg-[#07080B] border-white/10 h-8 w-36 text-xs" data-testid="code-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#101219] border-white/10">
                  {LANGS.map((l) => <SelectItem key={l} value={l} className="focus:bg-white/5 capitalize">{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[11px] font-mono text-white/30 ml-auto self-center">2 cr / generation</span>
            </div>
            <div className="flex gap-2">
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!streaming) generate(); } }}
                placeholder="Create a responsive hero section for a SaaS landing page using Tailwind CSS…"
                className="bg-[#07080B] border-white/10 resize-none flex-1" data-testid="code-prompt-input" />
              <Button onClick={generate} disabled={streaming} className="h-auto w-12 p-0 rounded-xl text-black border-0 shrink-0" style={{ background: CYAN }} data-testid="code-generate-btn">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* File tree */}
        <div className="lg:col-span-3 kr-card p-4 h-fit" data-testid="code-file-tree">
          <div className="kr-overline mb-3">FILES</div>
          <div className="space-y-1.5">
            {(session?.files || []).map((f, i) => (
              <button key={f.path} onClick={() => setViewFile({ ...f, index: i })}
                className="w-full flex items-center gap-2.5 p-2.5 rounded-lg border border-white/[0.07] hover:border-[#00E5FF]/40 text-left min-h-[44px]"
                data-testid={`code-file-${i}`}>
                <FileCode2 className="h-4 w-4 shrink-0" style={{ color: CYAN }} />
                <span className="text-xs font-mono truncate flex-1">{f.path}</span>
              </button>
            ))}
            {(!session || (session.files || []).length === 0) && <div className="text-xs text-white/35 py-4 text-center">Complete files generated with a path appear here.</div>}
          </div>
        </div>
      </div>

      {/* File viewer overlay */}
      {viewFile && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewFile(null)}>
          <div className="kr-card bg-[#0B0D12] w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="code-file-viewer">
            <div className="flex items-center gap-2 p-3.5 border-b border-white/[0.07]">
              <FileCode2 className="h-4 w-4" style={{ color: CYAN }} />
              <span className="text-sm font-mono flex-1 truncate">{viewFile.path}</span>
              <button onClick={() => copyFile(viewFile)} className="p-2 text-white/50 hover:text-white" aria-label="Copy file" data-testid="code-file-copy"><Copy className="h-4 w-4" /></button>
              <button onClick={() => downloadFile(viewFile)} className="p-2 text-white/50 hover:text-white" aria-label="Download file" data-testid="code-file-download"><Download className="h-4 w-4" /></button>
              <button onClick={() => setViewFile(null)} className="p-2 text-white/50 hover:text-white" aria-label="Close" data-testid="code-file-close"><X className="h-4 w-4" /></button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono leading-relaxed text-[#9EE8FF]">{viewFile.content}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
